/**
 * nav-config.ts — CF-style sidebar section builder.
 *
 * Dynamically reads the configured TanStack router (which owns ALL_FEATURES
 * as the route tree) and classifies every non-root route into a section
 * by keyword match against its slug.
 *
 * Pure function; deterministic; alpha-sorted within each section.
 * No side effects. Safe to call on every render.
 */
import { router } from "../../router";

export type NavItem = {
	label: string;
	to: string;
	icon: string;
};

export type NavSection = {
	section: string | null;
	items: NavItem[];
};

type RuleKey =
	| "Tracking"
	| "Partners"
	| "Conversions"
	| "Reports"
	| "Automation"
	| "Billing"
	| "Settings"
	| "More";

type Rule = {
	key: RuleKey;
	patterns: RegExp;
};

const RULES: Rule[] = [
	{
		key: "Tracking",
		patterns:
			/campaign|offer|tracking|smartlink|click-log|postback|goal|tier|group|bulk|template|state|clone/,
	},
	{
		key: "Partners",
		patterns:
			/affiliate|publisher|advertiser|agency|partner|manager|referral|invit/,
	},
	{
		key: "Conversions",
		patterns: /conversion|scrub|approv|reject|fraud|cap|payout|cps|commission/,
	},
	{
		key: "Reports",
		patterns:
			/report|metric|dashboard|widget|analytic|attribution|export|kpi/,
	},
	{
		key: "Automation",
		patterns: /automation|rule|schedul|permission|role|webhook/,
	},
	{
		key: "Billing",
		patterns: /billing|invoice|currency|tax|finance|payment/,
	},
	{
		key: "Settings",
		patterns: /setting|api-key|team|user|notif|brand|profile|theme/,
	},
];

const SECTION_ORDER: Array<string | null> = [
	null,
	"Tracking",
	"Partners",
	"Conversions",
	"Reports",
	"Automation",
	"Billing",
	"Settings",
	"More",
];

const SECTION_ICON: Record<string, string> = {
	Tracking: "Megaphone",
	Partners: "Users",
	Conversions: "Check",
	Reports: "BarChart",
	Automation: "Zap",
	Billing: "CreditCard",
	Settings: "Gear",
	More: "Layers",
};

function titleCase(slug: string): string {
	if (!slug) return "";
	return slug
		.replace(/^\/+|\/+$/g, "")
		.replace(/[-/.]/g, " ")
		.trim()
		.split(/\s+/)
		.map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
		.join(" ");
}

function classify(pathOrSlug: string): string | null {
	// Root index
	if (pathOrSlug === "/" || pathOrSlug === "") return null;
	const slug = pathOrSlug.replace(/^\/+/, "");
	for (const r of RULES) {
		if (r.patterns.test(slug)) return r.key;
	}
	return "More";
}

/**
 * Read every route path from the configured router and emit ordered sections.
 * Skips root. Drops duplicates. Alpha sorts within section.
 */
export function buildNavSections(): NavSection[] {
	// TanStack router exposes flatRoutes (array of { fullPath }) at runtime.
	// If the shape differs across versions, fall back gracefully.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const r = router as any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const candidates: any[] = Array.isArray(r?.flatRoutes)
		? r.flatRoutes
		: // eslint-disable-next-line @typescript-eslint/no-explicit-any
			Object.values(r?.routesByPath ?? {});

	const seen = new Set<string>();
	const items: NavItem[] = [];

	for (const rt of candidates) {
		if (!rt) continue;
		const rawPath: string | undefined = rt.fullPath ?? rt.path;
		if (typeof rawPath !== "string") continue;
		// Normalize: ensure leading slash, strip trailing slash (except "/")
		let path = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
		if (path !== "/" && path.endsWith("/")) path = path.slice(0, -1);
		if (seen.has(path)) continue;
		// Skip empty paths other than the root itself.
		if (path === "") continue;
		seen.add(path);

		const label = path === "/" ? "Overview" : titleCase(path);
		if (!label) continue;
		items.push({ label, to: path, icon: "" });
	}

	// Assemble
	const bucket = new Map<string | null, NavItem[]>();
	for (const it of items) {
		const section = classify(it.to);
		const icon =
			section === null ? "Home" : (SECTION_ICON[section] ?? "Layers");
		const existing = bucket.get(section) ?? [];
		existing.push({ ...it, icon });
		bucket.set(section, existing);
	}

	const out: NavSection[] = [];
	for (const sec of SECTION_ORDER) {
		const list = bucket.get(sec);
		if (!list || list.length === 0) continue;
		const sorted = [...list].sort((a, b) => a.label.localeCompare(b.label));
		out.push({ section: sec, items: sorted });
	}
	return out;
}
